// use-cloud-sync.ts — Supabase cloud-sync concern for useLifeOs.
// Owns sync status/user state, the sync-only refs (remoteReady, lastRemoteJson,
// pushTimer, syncUidRef), the signed-out reset, and the four sync effects:
//   1. auth observation, 2. retry listeners (online/focus/native foreground),
//   3. remote subscription + initial push, 4. debounced push on local change.
// Behaviour is identical to the original inline implementation in useLifeOs.ts;
// device-local persistence + multi-tab/indexed load stay in useLifeOs.

import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import {
  clearSyncedQueueItems,
  hasPendingSyncItems,
  mergeRemoteWithPendingQueues,
} from "../lib/lifeOsMutations";
import {
  decideRemoteSnapshotAction,
  isSameStateSnapshot,
  shouldSchedulePush,
} from "../lib/sync-decision-logic";
import {
  createDefaultState,
  createEmptyState,
  createLocalStateStorageKey,
  hasLocalState,
  loadLocalState,
} from "../lib/storage";
import { clearAllSessions as clearAiChatSessions } from "../lib/ai/ai-chat-store";
import { deleteIndexedState } from "../lib/indexeddb-storage";
import { logError } from "../lib/error-log";
import {
  getSyncPullAt, getSyncPushAt, isWipeOnSignOutEnabled, setSyncPullAt, setSyncPushAt,
} from "../lib/app-prefs";
import {
  addNativeForegroundRetry,
  INITIAL_SYNC_TIMEOUT_MS,
  isNativeCapacitorPlatform,
  loadSyncRuntime,
  withSyncTimeout,
} from "./life-os-sync-internals";
import type { NativeListenerHandle } from "./life-os-sync-internals";
import type { LifeOsState, SyncState } from "../types";

export interface CloudSyncCtx {
  syncEnabled: boolean;
  guestStorageKey: string;
  state: LifeOsState;
  setState: Dispatch<SetStateAction<LifeOsState>>;
  stateRef: MutableRefObject<LifeOsState>;
  persistStateNow: (nextState: LifeOsState, storageKey?: string) => boolean;
}

export function useCloudSync(ctx: CloudSyncCtx) {
  const { syncEnabled, guestStorageKey, state, setState, stateRef, persistStateNow } = ctx;

  const [syncState, setSyncState] = useState<SyncState>(() => (syncEnabled ? "connecting" : "local"));
  const [syncUser, setSyncUser] = useState<{ uid: string; email: string | null } | null>(null);
  const [retrySignal, setRetrySignal] = useState(0);
  // Sync diagnostics (Settings panel): timestamps of last successful push/pull.
  // Seed from persisted stamps so the diagnostics panel survives page reload.
  const [lastPushAt, setLastPushAt] = useState<string | null>(getSyncPushAt);
  const [lastPullAt, setLastPullAt] = useState<string | null>(getSyncPullAt);
  // updatedAt của remote snapshot đã thấy/đã push gần nhất — thay cho full-JSON
  // echo detection cũ (F3: không giữ và không stringify cả state chỉ để so echo).
  const lastRemoteUpdatedAt = useRef("");
  const remoteReady = useRef(false);
  const pushTimer = useRef<number | undefined>();
  const syncUidRef = useRef<string | undefined>();
  const syncUid = syncUser?.uid;

  const resetSignedOutGuestState = useCallback(() => {
    // Shared-device privacy: if enabled, drop this account's local cache on sign-out.
    // Skip when there are un-pushed changes so we never lose data not yet on cloud
    // (cloud is the source of truth → data returns on next login).
    const signingOutUid = syncUidRef.current;
    if (signingOutUid && isWipeOnSignOutEnabled() && !hasPendingSyncItems(stateRef.current)) {
      const key = createLocalStateStorageKey(signingOutUid);
      try { window.localStorage.removeItem(key); } catch { /* ignore */ }
      void deleteIndexedState(key);
    }
    const signedOutState = createDefaultState();
    syncUidRef.current = undefined;
    stateRef.current = signedOutState;
    lastRemoteUpdatedAt.current = "";
    remoteReady.current = false;
    // Wipe device-local AI chat history so one user's chats (which echo back
    // their personal data) never leak to the next account on this device.
    clearAiChatSessions();
    persistStateNow(signedOutState, guestStorageKey);
    setState(signedOutState);
    setSyncUser(null);
    setSyncState("auth");
  }, [guestStorageKey, persistStateNow, setState, stateRef]);

  const clearSyncedLocalQueues = useCallback(
    (syncedState: LifeOsState) => {
      setState((current) => {
        const nextState = clearSyncedQueueItems(current, syncedState);
        stateRef.current = nextState;
        return nextState;
      });
    },
    [setState, stateRef],
  );

  useEffect(() => {
    if (!syncEnabled) {
      remoteReady.current = true;
      return;
    }

    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    void loadSyncRuntime()
      .then(({ observeAuth }) => {
        if (cancelled) {
          return;
        }

        unsubscribe = observeAuth(
          (user) => {
            // P0 fix: Supabase fires TOKEN_REFRESHED (~hourly) and duplicate
            // SIGNED_IN events for the SAME user. Nothing changed — resetting
            // remoteReady below would permanently block pushes (the uid-keyed
            // subscription effect never re-runs, so nothing flips it back).
            if (user && user.uid === syncUidRef.current) {
              return;
            }

            window.clearTimeout(pushTimer.current);
            remoteReady.current = false;
            lastRemoteUpdatedAt.current = "";

            if (!user) {
              const wasSignedIn = Boolean(syncUidRef.current);
              if (wasSignedIn) {
                resetSignedOutGuestState();
                return;
              }
              syncUidRef.current = undefined;
              setSyncUser(null);
              setSyncState("auth");
              return;
            }

            const userStorageKey = createLocalStateStorageKey(user.uid);
            // A brand-new account must start EMPTY — never inherit the guest's
            // seeded demo data (the old `stateRef.current` fallback pushed the
            // "Xây Personal Life OS" sample set into every new account). If this
            // account already has cloud data, the remote subscription below pulls
            // it and merges over this empty base.
            const nextState = hasLocalState(userStorageKey) ? loadLocalState(userStorageKey) : createEmptyState();
            syncUidRef.current = user.uid;
            stateRef.current = nextState;
            persistStateNow(nextState, userStorageKey);
            setState(nextState);
            setSyncUser({ uid: user.uid, email: user.email });
            setSyncState("connecting");
          },
          () => { logError("sync", "auth observation error"); setSyncState("error"); },
        );
      })
      .catch((e) => { logError("sync", "auth runtime load failed", e instanceof Error ? e.stack : String(e)); setSyncState("error"); });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [persistStateNow, resetSignedOutGuestState, syncEnabled, setState, stateRef]);

  useEffect(() => {
    if (!syncEnabled) {
      return;
    }

    function requestRetry() {
      if (hasPendingSyncItems(stateRef.current)) {
        setRetrySignal((value) => value + 1);
      }
    }

    function requestForegroundRetry() {
      if (document.visibilityState === "visible") {
        requestRetry();
      }
    }

    window.addEventListener("online", requestRetry);
    window.addEventListener("focus", requestRetry);
    document.addEventListener("visibilitychange", requestForegroundRetry);

    let nativeAppStateHandle: NativeListenerHandle | undefined;
    let nativeListenerCancelled = false;
    if (isNativeCapacitorPlatform()) {
      void addNativeForegroundRetry(requestRetry)
        .then((handle) => {
          if (!handle) {
            return;
          }
          if (nativeListenerCancelled) {
            void handle.remove();
            return;
          }
          nativeAppStateHandle = handle;
        })
        .catch(() => undefined);
    }

    return () => {
      nativeListenerCancelled = true;
      void nativeAppStateHandle?.remove();
      window.removeEventListener("online", requestRetry);
      window.removeEventListener("focus", requestRetry);
      document.removeEventListener("visibilitychange", requestForegroundRetry);
    };
  }, [syncEnabled, stateRef]);

  useEffect(() => {
    if (!syncEnabled || !syncUid) {
      return;
    }

    let unsubscribe: (() => void) | undefined;
    let cancelled = false;
    const initialSyncTimeout = window.setTimeout(() => {
      if (!cancelled && syncUidRef.current === syncUid && !remoteReady.current) {
        setSyncState((current) => (current === "connecting" ? "offline" : current));
      }
    }, INITIAL_SYNC_TIMEOUT_MS);

    const clearInitialSyncTimeout = () => window.clearTimeout(initialSyncTimeout);

    void loadSyncRuntime()
      .then(({ pushRemoteState, subscribeToRemoteState }) => {
        if (cancelled) {
          clearInitialSyncTimeout();
          return;
        }

        function isCurrentSyncUser() {
          return syncUidRef.current === syncUid;
        }

        unsubscribe = subscribeToRemoteState(
          syncUid,
          (remote, meta) => {
            if (!isCurrentSyncUser()) {
              return;
            }

            clearInitialSyncTimeout();
            remoteReady.current = true;
            { const pulledAt = new Date().toISOString(); setLastPullAt(pulledAt); setSyncPullAt(pulledAt); }

            const currentState = stateRef.current;
            // Pure decision logic (tested in sync-decision-logic.test.ts) —
            // this handler only executes the chosen action.
            const action = decideRemoteSnapshotAction({
              remote,
              local: currentState,
              lastSeenRemoteUpdatedAt: lastRemoteUpdatedAt.current,
            });

            if (!remote || action === "first-push" || action === "push-pending") {
              const mode = !remote || action === "first-push" ? "all" : "pending";
              void withSyncTimeout(pushRemoteState(syncUid, currentState, mode))
                .then(() => {
                  if (!isCurrentSyncUser()) {
                    return;
                  }
                  lastRemoteUpdatedAt.current = currentState.updatedAt;
                  if (hasPendingSyncItems(currentState)) {
                    clearSyncedLocalQueues(currentState);
                  }
                  { const pushedAt = new Date().toISOString(); setLastPushAt(pushedAt); setSyncPushAt(pushedAt); }
                  setSyncState("synced");
                })
                .catch(() => setSyncState("offline"));
              return;
            }

            if (action === "skip-echo") {
              setSyncState("synced");
              return;
            }

            // "merge": LWW per-item — pending local edits/deletes CŨ HƠN bản
            // remote bị loại khỏi queue và nhận bản remote (F1).
            const nextState = mergeRemoteWithPendingQueues(remote, currentState, meta.itemUpdatedAt);
            // Echo of our own push: merge is deterministic, so identical content means
            // nothing changed — skip setState to avoid re-render + list churn.
            // (isSameStateSnapshot so updatedAt trước, chỉ stringify khi bằng nhau — F3.)
            if (isSameStateSnapshot(nextState, currentState)) {
              // Only record the echo when no queues are pending: the local dirty overlay
              // can mask a remote difference, and recording it would suppress the re-push
              // that eventually flushes the queues (stranded-queue edge after flaky push).
              if (!hasPendingSyncItems(nextState)) {
                lastRemoteUpdatedAt.current = remote.updatedAt;
              }
              setSyncState("synced");
              return;
            }
            lastRemoteUpdatedAt.current = remote.updatedAt;
            stateRef.current = nextState;
            persistStateNow(nextState, createLocalStateStorageKey(syncUid));
            setState(nextState);
            setSyncState("synced");
          },
          () => {
            if (syncUidRef.current !== syncUid) {
              return;
            }
            clearInitialSyncTimeout();
            remoteReady.current = true;
            logError("sync", "realtime subscription error");
            setSyncState("error");
          },
        );
      })
      .catch((e) => {
        clearInitialSyncTimeout();
        remoteReady.current = true;
        logError("sync", "sync runtime load failed", e instanceof Error ? e.stack : String(e));
        setSyncState("error");
      });

    return () => {
      cancelled = true;
      clearInitialSyncTimeout();
      unsubscribe?.();
    };
    // retrySignal: nút "Thử lại" / network-restore re-subscribes — kênh realtime
    // có thể đã chết sau khi offline, chỉ re-arm push là không đủ.
  }, [persistStateNow, syncEnabled, syncUid, retrySignal, setState, stateRef, clearSyncedLocalQueues]);

  // Debounced push when local state changes (cloud half of the original combined
  // persist+push effect; the local persist half lives in useLifeOs).
  useEffect(() => {
    if (!syncEnabled || !syncUid || !remoteReady.current) {
      return;
    }

    // F3: gate rẻ theo pending queue (mọi mutation đều đi qua markDirty/markDeleted)
    // — KHÔNG JSON.stringify full state trên mỗi lần state đổi nữa. Queue được
    // clear sau push thành công nên effect tự tắt, không lặp vô hạn.
    if (!shouldSchedulePush(state)) {
      return;
    }

    setSyncState("connecting");
    window.clearTimeout(pushTimer.current);
    pushTimer.current = window.setTimeout(() => {
      loadSyncRuntime()
        .then(({ pushRemoteState }) => withSyncTimeout(pushRemoteState(syncUid, state)))
        .then(() => {
          if (syncUidRef.current !== syncUid) {
            return;
          }
          lastRemoteUpdatedAt.current = state.updatedAt;
          clearSyncedLocalQueues(state);
          setLastPushAt(new Date().toISOString());
          setSyncState("synced");
        })
        .catch(() => setSyncState("offline"));
    }, 400);
  }, [retrySignal, state, syncEnabled, syncUid, clearSyncedLocalQueues]);

  const retrySync = useCallback(() => setRetrySignal((value) => value + 1), []);

  return { syncState, syncUser, lastPushAt, lastPullAt, retrySync, resetSignedOutGuestState };
}
